import WGS84Service from '@/services/wgs84ProjectionService';
import MapService from '@/services/mapService';
import { mapState } from 'vuex';
import { randomInt, isInternetExplorer11 } from '@/utils/vuex';
import moment from 'moment';
import get from 'lodash/get';

const mapDefaultZoom = 16.5;

export default {
  props: {
    geoCoordinates: {
      type: Array,
      required: true,
    },
    zoom: {
      type: Number,
      required: false,
      default: -1,
    },
    animateFlyIn: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    allowScrollZoom: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowMultiCoordinates: {
      type: Boolean,
      required: false,
      default: false,
    },
    multiCoordinatesIcon: {
      type: String,
      required: false,
      default: 'sensor',
    },
    dtwData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    overlayInfo: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      initialBearing: randomInt(360),
      mapAccessToken: MapService.getAccessToken(this.$store.state.app.UiTheme ? 'light' : 'dark'),
      light: 'mapbox://styles/mapbox/light-v9',
      dark: 'mapbox://styles/mapbox/dark-v10',
      dmStreets: 'mapbox://styles/digimondo/cjn4e7kzsjv8w2rlc0a574xu4',
      satellite: 'mapbox://styles/mapbox/satellite-v9',
      streets: 'mapbox://styles/mapbox/streets-v11',
      duration: 2000,
      center: this.allowMultiCoordinates ? this.geoCoordinates[0].coordinates : this.geoCoordinates,

      // any animations in the map are overwritten if a new one starts (`flyTo`)
      // as they return a promise, keep this queue going to start one animation after another
      animationQueue: [],

      zoomToUseFallback: mapDefaultZoom, // 12 for "header"
      zoomOffset: 0, // -1.5 for "header"
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    isNotIE11() {
      return !isInternetExplorer11();
    },
    hasMapIconAttachment() {
      return get(this, 'dtwData.mapIconAttachmentId') && this.dtwData.mapIconAttachmentId.trim().length > 0;
    },
    hasMapMarkerIcon() {
      return get(this, 'dtwData.faIcon') && this.dtwData.faIcon.trim().length > 0;
    },
    markerColor() {
      return MapService.markerColor(this.color, this.$store);
    },
    mapStyle() {
      if (!this.$store.state.app.mapStyle) {
        // TODO: use proper store.commit action to mutate vuex store
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.$store.state.app.mapStyle = localStorage.getItem('mapStyle');
      }
      switch (this.$store.state.app.mapStyle) {
        case '1':
          return !this.$store.state.app.UiTheme ? this.light : this.dark;
        case '2':
          return this.satellite;
        case '3':
          return this.streets;
        case '4':
          return this.dmStreets;
        default:
          return !this.$store.state.app.UiTheme ? this.light : this.dark;
      }
    },
  },
  watch: {
    overlayInfo() {
      this.drawOverlay();
    },
    markerCoordinates() {
      if (!this.map) {
        return;
      }

      const flyToCoordinate = this.markerCoordinates;
      const lon = parseFloat(get(flyToCoordinate, '[0]'));
      const lat = parseFloat(get(flyToCoordinate, '[1]'));
      if (isNaN(lon) || isNaN(lat) || lon < -180 || lon > 180 || lat < -90 || lat > 90) {
        return;
      }
      const currentCenter = this.map.getCenter();
      // no need to fly anywhere if the coordinate does not change for more than a few centimeters
      // also, do not interrupt the initial "fly-in" animation
      if (
        Math.abs(currentCenter.lng - flyToCoordinate[0]) < 0.00001 &&
        Math.abs(currentCenter.lat - flyToCoordinate[1]) < 0.00001
      ) {
        return;
      }

      const zoomToUse = this.calculateZoomToUse();

      const inBounds = (bounds, lnglat) => {
        let lng;
        const multLng = (lnglat[0] - bounds['_ne']['lng']) * (lnglat[0] - bounds['_sw']['lng']);
        if (bounds['_ne']['lng'] > bounds['_sw']['lng']) {
          lng = multLng < 0;
        } else {
          lng = multLng > 0;
        }
        const lat = (lnglat[1] - bounds['_ne']['lat']) * (lnglat[1] - bounds['_sw']['lat']) < 0;
        return lng && lat;
      };

      // do not fly to coordinates, if the marker is still visible
      const mapBounds = this.map.getBounds();
      if (inBounds(mapBounds, flyToCoordinate)) {
        this.addToAnimationQueue({
          center: flyToCoordinate,
          duration: this.animateFlyIn ? 500 : 0,
          animate: this.animateFlyIn,
        });
        return;
      }

      this.addToAnimationQueue({
        center: flyToCoordinate,
        pitch: 0,
        bearing: 0,
        zoom: zoomToUse,
        duration: this.animateFlyIn ? 500 : 0,
        animate: this.animateFlyIn,
      });
    },
    allowScrollZoom() {
      if (!this.allowScrollZoom) {
        this.map.scrollZoom.disable();
      } else {
        this.map.scrollZoom.enable();
      }
    },
  },
  methods: {
    get,
    mapMarkerIconUrl(dtwData) {
      return `${process.env.VUE_APP_API_HOST}${process.env.VUE_APP_API_PATH}/upload/${dtwData.mapIconAttachmentId}`;
    },

    addToAnimationQueue(params) {
      this.animationQueue.push(params);
      if (!this.map.isMoving()) {
        this.processAnimationQueue();
      }
    },
    processAnimationQueue() {
      if (this.animationQueue.length > 0) {
        const firstEntry = this.animationQueue[0];
        this.animationQueue.shift();
        this.map.flyTo(firstEntry);
      }
    },

    calculateZoomToUse() {
      let zoomToUse = this.zoom;
      if (zoomToUse < 0) {
        zoomToUse = this.zoomToUseFallback;
        try {
          if (this.geoPolygon.length > 0 && this.geoPolygon[0].length >= 2) {
            let northEast = { lat: -90, lon: -360 };
            let southWest = { lat: 90, lon: 360 };

            this.geoPolygon[0].forEach((c) => {
              northEast.lat = Math.max(northEast.lat, c[1]);
              southWest.lat = Math.min(southWest.lat, c[1]);
              northEast.lon = Math.max(northEast.lon, c[0]);
              southWest.lon = Math.min(southWest.lon, c[0]);
            });
            const mapContainer = this.$refs.twinMap.$refs.container;
            const mapDimensions = { width: mapContainer.clientWidth - 30, height: mapContainer.clientHeight - 30 };
            zoomToUse = WGS84Service.boundsZoomLevel({ northEast, southWest }, mapDimensions) + this.zoomOffset;
          }
          // eslint-disable-next-line no-empty
        } catch (error) {}
      }
      return zoomToUse;
    },

    drawOverlay() {
      if (!this.map) {
        return;
      }
      if (!this.map.getSource('empty')) {
        // since there is no direct z-index property, create dummy layers under which stuff is positioned
        // drawOverlay should be called before any draw-controls for polygons are added,
        // as to make sure this is really at the bottom
        this.map.addSource('empty', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        this.map.addLayer({
          id: 'groundLayer',
          type: 'symbol',
          source: 'empty',
        });
      }
      const overlayId = 'overlay';
      if (this.map.getLayer(overlayId)) {
        this.map.removeLayer(overlayId);
      }
      if (this.map.getSource(overlayId)) {
        this.map.removeSource(overlayId);
      }
      if (!this.overlayInfo) {
        return;
      }
      this.map.addSource(overlayId, {
        type: 'image',
        url: this.overlayInfo.image,
        coordinates: this.overlayInfo.coordinates,
      });
      this.map.addLayer(
        {
          id: overlayId,
          source: overlayId,
          type: 'raster',
          paint: { 'raster-opacity': 1.0 },
        },
        'groundLayer'
      );
    },

    localizeDate(a) {
      if (a) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(a).format('L LTS');
      }
      return '-';
    },
  },
};
